/**
 * A minimal binary glTF (.glb) reader for the Shader node's 3D input.
 *
 * It keeps only what a shader needs — triangle positions, normals and UVs with
 * the scene's node transforms baked in — as plain typed arrays, so the sandbox
 * can build geometry in any runtime without a glTF loader. Compressed meshes
 * (Draco, meshopt) need a decoder and are rejected with a clear message.
 */

import type { ShaderModelData, ShaderModelMesh } from './contract'

const GLB_MAGIC = 0x46546c67 // 'glTF'
const CHUNK_JSON = 0x4e4f534a
const CHUNK_BIN = 0x004e4942

const MODE_TRIANGLES = 4
const MODE_TRIANGLE_STRIP = 5
const MODE_TRIANGLE_FAN = 6

const UNSUPPORTED_EXTENSIONS: Record<string, string> = {
  KHR_draco_mesh_compression: 'Draco-compressed',
  EXT_meshopt_compression: 'meshopt-compressed',
}

const COMPONENT_SIZE: Record<number, number> = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 }
const TYPE_SIZE: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4 }

interface GltfAccessor {
  bufferView?: number
  byteOffset?: number
  componentType: number
  normalized?: boolean
  count: number
  type: string
  sparse?: {
    count: number
    indices: { bufferView: number; byteOffset?: number; componentType: number }
    values: { bufferView: number; byteOffset?: number }
  }
}

interface GltfBufferView {
  buffer: number
  byteOffset?: number
  byteLength: number
  byteStride?: number
}

interface GltfPrimitive {
  attributes: Record<string, number>
  indices?: number
  mode?: number
  extensions?: Record<string, unknown>
}

interface GltfNode {
  mesh?: number
  children?: number[]
  matrix?: number[]
  translation?: number[]
  rotation?: number[]
  scale?: number[]
}

interface GltfDocument {
  scene?: number
  scenes?: { nodes?: number[] }[]
  nodes?: GltfNode[]
  meshes?: { primitives?: GltfPrimitive[] }[]
  accessors?: GltfAccessor[]
  bufferViews?: GltfBufferView[]
  buffers?: { byteLength: number; uri?: string }[]
  extensionsUsed?: string[]
  extensionsRequired?: string[]
}

/** Column-major 4×4. */
type Mat4 = Float64Array

export class GlbParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GlbParseError'
  }
}

/** Parse a glTF 2.0 binary into world-space triangle meshes and their bounds. */
export function parseGlb(buffer: ArrayBuffer): ShaderModelData {
  const { json, bin } = readChunks(buffer)
  let doc: GltfDocument
  try {
    doc = JSON.parse(new TextDecoder().decode(json)) as GltfDocument
  } catch {
    throw new GlbParseError('The model’s glTF description is not valid JSON.')
  }
  if (!doc || typeof doc !== 'object') throw new GlbParseError('The model’s glTF description is empty.')
  rejectCompression(doc)

  const buffers = (doc.buffers ?? []).map((b, i) => resolveBuffer(b, i, bin))
  const reader = new AccessorReader(doc, buffers)
  const meshes: ShaderModelMesh[] = []

  const visit = (nodeIndex: number, parent: Mat4, path: Set<number>) => {
    if (path.has(nodeIndex)) throw new GlbParseError('The model’s node hierarchy contains a cycle.')
    const node = at(doc.nodes, nodeIndex, 'node')
    const world = multiply(parent, localMatrix(node))
    if (node.mesh !== undefined) meshes.push(...buildMeshes(doc, reader, node.mesh, world))
    path.add(nodeIndex)
    for (const child of node.children ?? []) visit(child, world, path)
    path.delete(nodeIndex)
  }
  for (const root of sceneRoots(doc)) visit(root, identity(), new Set())

  // A file with meshes but no nodes still has geometry worth showing.
  if (meshes.length === 0 && !(doc.nodes?.length)) {
    for (let i = 0; i < (doc.meshes?.length ?? 0); i++) meshes.push(...buildMeshes(doc, reader, i, identity()))
  }
  if (meshes.length === 0) throw new GlbParseError('The model contains no triangle meshes.')
  return { meshes, bounds: computeBounds(meshes) }
}

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

function readChunks(buffer: ArrayBuffer): { json: Uint8Array; bin: Uint8Array | null } {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 20) throw new GlbParseError('This is not a binary glTF (.glb) file.')
  const view = new DataView(buffer)
  if (view.getUint32(0, true) !== GLB_MAGIC) throw new GlbParseError('This is not a binary glTF (.glb) file.')
  const version = view.getUint32(4, true)
  if (version !== 2) throw new GlbParseError(`Only glTF 2.0 models are supported (this file is version ${version}).`)
  const length = Math.min(view.getUint32(8, true), buffer.byteLength)

  let json: Uint8Array | null = null
  let bin: Uint8Array | null = null
  let offset = 12
  while (offset + 8 <= length) {
    const chunkLength = view.getUint32(offset, true)
    const chunkType = view.getUint32(offset + 4, true)
    const start = offset + 8
    if (start + chunkLength > length) throw new GlbParseError('The .glb file is truncated.')
    if (chunkType === CHUNK_JSON && !json) json = new Uint8Array(buffer, start, chunkLength)
    else if (chunkType === CHUNK_BIN && !bin) bin = new Uint8Array(buffer, start, chunkLength)
    offset = start + chunkLength
  }
  if (!json) throw new GlbParseError('The .glb file has no JSON chunk.')
  return { json, bin }
}

function rejectCompression(doc: GltfDocument) {
  const used = new Set([...(doc.extensionsUsed ?? []), ...(doc.extensionsRequired ?? [])])
  for (const mesh of doc.meshes ?? []) {
    for (const primitive of mesh.primitives ?? []) for (const name of Object.keys(primitive.extensions ?? {})) used.add(name)
  }
  for (const [name, label] of Object.entries(UNSUPPORTED_EXTENSIONS)) {
    if (used.has(name)) throw new GlbParseError(`${label} models are not supported yet (${name}). Export the model without mesh compression.`)
  }
}

function resolveBuffer(buffer: { byteLength: number; uri?: string }, index: number, bin: Uint8Array | null): Uint8Array {
  if (buffer.uri === undefined) {
    if (index !== 0 || !bin) throw new GlbParseError('The .glb file is missing its binary chunk.')
    return bin
  }
  const match = /^data:[^,]*;base64,(.*)$/.exec(buffer.uri)
  if (!match) throw new GlbParseError('The model references external files. Export it as a single self-contained .glb.')
  const text = atob(match[1])
  const bytes = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i)
  return bytes
}

function at<T>(list: T[] | undefined, index: number, what: string): T {
  const item = Number.isInteger(index) ? list?.[index] : undefined
  if (item === undefined || item === null) throw new GlbParseError(`The model refers to a missing ${what} (${index}).`)
  return item
}

function sceneRoots(doc: GltfDocument): number[] {
  const scenes = doc.scenes ?? []
  if (scenes.length > 0) return at(scenes, doc.scene ?? 0, 'scene').nodes ?? []
  // No scene: every node that is nobody's child is a root.
  const children = new Set((doc.nodes ?? []).flatMap((n) => n.children ?? []))
  return (doc.nodes ?? []).map((_, i) => i).filter((i) => !children.has(i))
}

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

class AccessorReader {
  private readonly views: DataView[]

  constructor(private readonly doc: GltfDocument, private readonly buffers: Uint8Array[]) {
    this.views = buffers.map((b) => new DataView(b.buffer, b.byteOffset, b.byteLength))
  }

  /** Every element as floats, `count × components` long. */
  floats(index: number): { data: Float32Array; components: number; count: number } {
    const accessor = at(this.doc.accessors, index, 'accessor')
    const components = TYPE_SIZE[accessor.type]
    if (!components) throw new GlbParseError(`Unsupported accessor type ${accessor.type}.`)
    const count = accessor.count
    const data = new Float32Array(count * components)
    if (accessor.bufferView !== undefined) {
      this.read(accessor.bufferView, accessor.byteOffset ?? 0, accessor.componentType, components, count, (i, v) => {
        data[i] = normalize(v, accessor.componentType, accessor.normalized === true)
      })
    }
    if (accessor.sparse) {
      const { sparse } = accessor
      const targets: number[] = []
      this.read(sparse.indices.bufferView, sparse.indices.byteOffset ?? 0, sparse.indices.componentType, 1, sparse.count, (_, v) => targets.push(v))
      this.read(sparse.values.bufferView, sparse.values.byteOffset ?? 0, accessor.componentType, components, sparse.count, (i, v) => {
        const target = targets[Math.floor(i / components)]
        if (target < count) data[target * components + (i % components)] = normalize(v, accessor.componentType, accessor.normalized === true)
      })
    }
    return { data, components, count }
  }

  indices(index: number): Uint32Array {
    const accessor = at(this.doc.accessors, index, 'accessor')
    if (![5121, 5123, 5125].includes(accessor.componentType)) throw new GlbParseError('Mesh indices must be unsigned integers.')
    const out = new Uint32Array(accessor.count)
    if (accessor.bufferView !== undefined) this.read(accessor.bufferView, accessor.byteOffset ?? 0, accessor.componentType, 1, accessor.count, (i, v) => { out[i] = v })
    return out
  }

  private read(viewIndex: number, byteOffset: number, componentType: number, components: number, count: number, write: (i: number, value: number) => void) {
    const view = at(this.doc.bufferViews, viewIndex, 'buffer view')
    const buffer = at(this.views, view.buffer, 'buffer')
    const size = COMPONENT_SIZE[componentType]
    if (!size) throw new GlbParseError(`Unsupported component type ${componentType}.`)
    const elementSize = size * components
    const stride = view.byteStride || elementSize
    const start = (view.byteOffset ?? 0) + byteOffset
    const end = (view.byteOffset ?? 0) + view.byteLength
    if (count > 0 && (start + stride * (count - 1) + elementSize > end || end > buffer.byteLength)) {
      throw new GlbParseError('The model’s data runs past the end of its buffer.')
    }
    for (let e = 0; e < count; e++) {
      const base = start + e * stride
      for (let c = 0; c < components; c++) write(e * components + c, readComponent(buffer, base + c * size, componentType))
    }
  }
}

function readComponent(view: DataView, offset: number, componentType: number): number {
  switch (componentType) {
    case 5120: return view.getInt8(offset)
    case 5121: return view.getUint8(offset)
    case 5122: return view.getInt16(offset, true)
    case 5123: return view.getUint16(offset, true)
    case 5125: return view.getUint32(offset, true)
    default: return view.getFloat32(offset, true)
  }
}

/** Normalised integers map to -1..1 (signed) or 0..1 (unsigned), per the glTF spec. */
function normalize(value: number, componentType: number, normalized: boolean): number {
  if (!normalized) return value
  switch (componentType) {
    case 5120: return Math.max(value / 127, -1)
    case 5121: return value / 255
    case 5122: return Math.max(value / 32767, -1)
    case 5123: return value / 65535
    case 5125: return value / 4294967295
    default: return value
  }
}

// ---------------------------------------------------------------------------
// Meshes
// ---------------------------------------------------------------------------

function buildMeshes(doc: GltfDocument, reader: AccessorReader, meshIndex: number, world: Mat4): ShaderModelMesh[] {
  const mesh = at(doc.meshes, meshIndex, 'mesh')
  const out: ShaderModelMesh[] = []
  const normalMatrix = normalMatrixOf(world)
  const mirrored = determinant3(world) < 0
  for (const primitive of mesh.primitives ?? []) {
    const mode = primitive.mode ?? MODE_TRIANGLES
    if (mode !== MODE_TRIANGLES && mode !== MODE_TRIANGLE_STRIP && mode !== MODE_TRIANGLE_FAN) continue
    const positionIndex = primitive.attributes?.POSITION
    if (positionIndex === undefined) continue

    const position = reader.floats(positionIndex)
    if (position.components !== 3) throw new GlbParseError('Mesh positions must be 3D vectors.')
    const vertexCount = position.count
    const positions = transformPoints(position.data, world)

    let normals: Float32Array | null = null
    if (primitive.attributes.NORMAL !== undefined) {
      const normal = reader.floats(primitive.attributes.NORMAL)
      if (normal.components === 3 && normal.count === vertexCount) normals = transformNormals(normal.data, normalMatrix)
    }

    let uvs: Float32Array | null = null
    if (primitive.attributes.TEXCOORD_0 !== undefined) {
      const uv = reader.floats(primitive.attributes.TEXCOORD_0)
      if (uv.components === 2 && uv.count === vertexCount) uvs = uv.data
    }

    let indices: Uint32Array | null = primitive.indices !== undefined ? reader.indices(primitive.indices) : null
    if (indices) {
      for (let i = 0; i < indices.length; i++) {
        if (indices[i] >= vertexCount) throw new GlbParseError('A mesh index points past the end of its vertices.')
      }
    }
    if (mode !== MODE_TRIANGLES) indices = toTriangleList(indices ?? sequence(vertexCount), mode)
    // A mirroring transform turns counter-clockwise triangles clockwise (glTF spec). The transform
    // is baked in, so no renderer can tell: reverse the winding here or culling hides the faces.
    if (mirrored) indices = reverseWinding(indices ?? sequence(vertexCount))
    out.push({ positions, normals, uvs, indices })
  }
  return out
}

function sequence(count: number): Uint32Array {
  const out = new Uint32Array(count)
  for (let i = 0; i < count; i++) out[i] = i
  return out
}

/** Swap each triangle's last two corners, in a copy (the reader may share its arrays). */
function reverseWinding(source: Uint32Array): Uint32Array {
  const out = Uint32Array.from(source)
  for (let i = 0; i + 2 < out.length; i += 3) {
    out[i + 1] = source[i + 2]
    out[i + 2] = source[i + 1]
  }
  return out
}

function toTriangleList(source: Uint32Array, mode: number): Uint32Array {
  const triangles = Math.max(0, source.length - 2)
  const out = new Uint32Array(triangles * 3)
  for (let i = 0; i < triangles; i++) {
    if (mode === MODE_TRIANGLE_FAN) out.set([source[0], source[i + 1], source[i + 2]], i * 3)
    // Every other strip triangle is reversed to keep a consistent winding.
    else if (i % 2 === 0) out.set([source[i], source[i + 1], source[i + 2]], i * 3)
    else out.set([source[i + 1], source[i], source[i + 2]], i * 3)
  }
  return out
}

function computeBounds(meshes: ShaderModelMesh[]): ShaderModelData['bounds'] {
  const min: [number, number, number] = [Infinity, Infinity, Infinity]
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity]
  for (const { positions } of meshes) {
    for (let i = 0; i + 2 < positions.length; i += 3) {
      for (let c = 0; c < 3; c++) {
        const v = positions[i + c]
        if (v < min[c]) min[c] = v
        if (v > max[c]) max[c] = v
      }
    }
  }
  if (!Number.isFinite(min[0])) return { min: [0, 0, 0], max: [0, 0, 0] }
  return { min, max }
}

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

function identity(): Mat4 {
  const m = new Float64Array(16)
  m[0] = m[5] = m[10] = m[15] = 1
  return m
}

function localMatrix(node: GltfNode): Mat4 {
  if (Array.isArray(node.matrix) && node.matrix.length === 16) return Float64Array.from(node.matrix)
  const [tx, ty, tz] = node.translation ?? [0, 0, 0]
  const [x, y, z, w] = node.rotation ?? [0, 0, 0, 1]
  const [sx, sy, sz] = node.scale ?? [1, 1, 1]
  const m = new Float64Array(16)
  m[0] = (1 - 2 * (y * y + z * z)) * sx
  m[1] = 2 * (x * y + z * w) * sx
  m[2] = 2 * (x * z - y * w) * sx
  m[4] = 2 * (x * y - z * w) * sy
  m[5] = (1 - 2 * (x * x + z * z)) * sy
  m[6] = 2 * (y * z + x * w) * sy
  m[8] = 2 * (x * z + y * w) * sz
  m[9] = 2 * (y * z - x * w) * sz
  m[10] = (1 - 2 * (x * x + y * y)) * sz
  m[12] = tx
  m[13] = ty
  m[14] = tz
  m[15] = 1
  return m
}

function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Float64Array(16)
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row] * b[col * 4 + k]
      out[col * 4 + row] = sum
    }
  }
  return out
}

/** Determinant of the upper 3×3: negative when the transform mirrors. */
function determinant3(m: Mat4): number {
  return m[0] * (m[5] * m[10] - m[9] * m[6]) - m[4] * (m[1] * m[10] - m[9] * m[2]) + m[8] * (m[1] * m[6] - m[5] * m[2])
}

/** Inverse-transpose of the upper 3×3 (row-major 3×3 result), up to scale — normals are renormalised. */
function normalMatrixOf(m: Mat4): Float64Array {
  const a = m[0], b = m[4], c = m[8]
  const d = m[1], e = m[5], f = m[9]
  const g = m[2], h = m[6], i = m[10]
  const cof = new Float64Array([
    e * i - f * h, -(d * i - f * g), d * h - e * g,
    -(b * i - c * h), a * i - c * g, -(a * h - b * g),
    b * f - c * e, -(a * f - c * d), a * e - b * d,
  ])
  // (M⁻¹)ᵀ = cofactor / det: keep det's sign so mirrored nodes don't flip their normals inward.
  const det = a * cof[0] + b * cof[1] + c * cof[2]
  if (det < 0) for (let k = 0; k < 9; k++) cof[k] = -cof[k]
  return cof
}

function transformPoints(points: Float32Array, m: Mat4): Float32Array {
  const out = new Float32Array(points.length)
  for (let i = 0; i + 2 < points.length; i += 3) {
    const x = points[i], y = points[i + 1], z = points[i + 2]
    out[i] = m[0] * x + m[4] * y + m[8] * z + m[12]
    out[i + 1] = m[1] * x + m[5] * y + m[9] * z + m[13]
    out[i + 2] = m[2] * x + m[6] * y + m[10] * z + m[14]
  }
  return out
}

function transformNormals(normals: Float32Array, n: Float64Array): Float32Array {
  const out = new Float32Array(normals.length)
  for (let i = 0; i + 2 < normals.length; i += 3) {
    const x = normals[i], y = normals[i + 1], z = normals[i + 2]
    const nx = n[0] * x + n[1] * y + n[2] * z
    const ny = n[3] * x + n[4] * y + n[5] * z
    const nz = n[6] * x + n[7] * y + n[8] * z
    const length = Math.hypot(nx, ny, nz) || 1
    out[i] = nx / length
    out[i + 1] = ny / length
    out[i + 2] = nz / length
  }
  return out
}
